import { Campaign, PopulatedCampaign } from "@api/types";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { Separator } from "@/components/ui/separator";
import { cn, formatCurrency, formatNumber, formatPercent, pluralize } from "@/lib/utils";

interface CampaignMetricProps {
  name: string;
  value: number;
  formatter?: (value: number) => string;
  size?: "sm" | "lg";
  animate?: boolean;
  explainer?: string;
  numberClassNames?: string;
}

interface CampaignMetricsProps {
  campaign: Campaign | PopulatedCampaign;
  size?: "sm" | "lg";
  animate?: boolean;
}

export function CampaignMetric({
  name,
  value,
  formatter,
  size = "lg",
  animate = true,
  explainer,
  numberClassNames,
}: CampaignMetricProps) {
  return (
    <div className="shrink-0 text-center">
      <div
        className={cn(
          "font-medium",
          size === "sm"
            ? "text-[10px] leading-tight text-muted-foreground"
            : "text-lg/6 sm:text-sm/6 mb-1",
        )}
      >
        {name}
      </div>
      {animate ? (
        <AnimatedNumber
          className={cn(
            "tabular-nums mt-1 font-semibold",
            size === "lg" ? "text-3xl/8 sm:text-2xl/8" : "text-xs",
            numberClassNames,
          )}
          to={value}
          formatter={formatter}
        />
      ) : (
        <div
          className={cn(
            "tabular-nums mt-1 font-semibold",
            size === "lg" ? "text-3xl/8 sm:text-2xl/8" : "text-xs",
            numberClassNames,
          )}
        >
          {formatter ? formatter(value) : value}
        </div>
      )}
      {size === "lg" && explainer && (
        <div className="text-muted-foreground text-xs">{explainer}</div>
      )}
    </div>
  );
}

export function CampaignMetrics({ campaign, size = "lg", animate = true }: CampaignMetricsProps) {
  const isEmail = campaign.protocols?.some((protocol) => protocol.type === "email") ?? false;
  // Email campaigns get true delivery counts from Postmark webhooks; SMS (and
  // older email campaigns sent before tracking) fall back to sent minus failed.
  const hasDeliveryTracking = isEmail && (campaign.stats.delivered ?? 0) > 0;
  const delivered = hasDeliveryTracking
    ? (campaign.stats.delivered ?? 0)
    : campaign.stats.sent - campaign.stats.failed;
  const opened = campaign.stats.opened ?? 0;
  // Always surface opens for email so the tracking is visible even at 0 (e.g. a
  // blast that was just sent); SMS has no open tracking so it stays hidden.
  const showOpens = isEmail;
  // Email link clicks are tracked by Postmark, so `campaign.links` (our SMS link
  // shortener output) is empty — surface clicks for any email, mirroring opens.
  const showClicks = isEmail || campaign.links.length > 0;
  const replies = campaign.stats.replies / delivered;
  const clicks = campaign.stats.clicks / delivered;
  const optout = campaign.stats.optout / delivered;

  if (campaign.status === "draft" || campaign.status === "scheduled") return null;
  return (
    // max-w-full + overflow-x-auto: the strip is wider than a phone screen,
    // so it scrolls horizontally instead of blowing out the page width.
    <div
      className={cn(
        "w-fit max-w-full flex items-center overflow-x-auto border rounded-md bg-background",
        size === "sm" ? "gap-2.5 px-2.5 py-1" : "gap-8 px-4 py-2 md:px-7",
      )}
    >
      {campaign.status === "complete" ? (
        <>
          <CampaignMetric
            name="Delivered"
            value={campaign.stats.sent > 0 ? delivered / campaign.stats.sent : 0}
            formatter={(value) => formatPercent(value, { precision: 0 })}
            size={size}
            animate={animate}
            explainer={`${formatNumber(delivered)} delivered`}
          />
          <Separator orientation="vertical" className={cn(size === "sm" ? "h-7" : "h-10")} />
          {showOpens && (
            <>
              <CampaignMetric
                name="Opens"
                value={delivered > 0 ? opened / delivered : 0}
                formatter={(value) => formatPercent(value, { precision: 0 })}
                size={size}
                animate={animate}
                explainer={`${formatNumber(opened)} ${pluralize("open", opened)}`}
              />
              <Separator orientation="vertical" className={cn(size === "sm" ? "h-7" : "h-10")} />
            </>
          )}
          <CampaignMetric
            name="Replies"
            value={replies}
            formatter={(value) => formatPercent(value, { precision: 0 })}
            size={size}
            animate={animate}
            explainer={`${formatNumber(campaign.stats.replies)} ${pluralize("reply", campaign.stats.replies)}`}
          />
          <Separator orientation="vertical" className={cn(size === "sm" ? "h-7" : "h-10")} />
          {showClicks && (
            <>
              <CampaignMetric
                name="Clicks"
                value={clicks}
                formatter={(value) => formatPercent(value, { precision: 0 })}
                size={size}
                animate={animate}
                explainer={`${formatNumber(campaign.stats.clicks)} ${pluralize("click", campaign.stats.clicks)}`}
              />
              <Separator orientation="vertical" className={cn(size === "sm" ? "h-7" : "h-10")} />
            </>
          )}
          <CampaignMetric
            name="Opt out"
            value={optout}
            formatter={(value) => formatPercent(value, { precision: 0 })}
            size={size}
            animate={animate}
            explainer={`${formatNumber(campaign.stats.optout)} ${pluralize("opt out", campaign.stats.optout)}`}
          />
          {((campaign.stats.conversionCount ?? 0) > 0 ||
            (campaign.stats.conversionRevenue ?? 0) > 0) && (
            <>
              <Separator orientation="vertical" className={cn(size === "sm" ? "h-7" : "h-10")} />
              <CampaignMetric
                name="Conversions"
                value={campaign.stats.conversionCount ?? 0}
                formatter={(value) => formatNumber(value, { precision: 0 })}
                size={size}
                animate={animate}
                explainer={formatCurrency(campaign.stats.conversionRevenue ?? 0)}
              />
            </>
          )}
        </>
      ) : (
        <>
          <CampaignMetric
            name="Queued"
            value={campaign.stats.queued}
            formatter={(value) => formatNumber(value, { precision: 0 })}
            size={size}
            animate={animate}
          />
          <Separator orientation="vertical" className={cn(size === "sm" ? "h-7" : "h-10")} />
          <CampaignMetric
            name="Sent"
            value={campaign.stats.sent}
            formatter={(value) => formatNumber(value, { precision: 0 })}
            size={size}
            animate={animate}
          />
          <Separator orientation="vertical" className={cn(size === "sm" ? "h-7" : "h-10")} />
          <CampaignMetric
            name="Failed"
            value={campaign.stats.failed}
            formatter={(value) => formatNumber(value, { precision: 0 })}
            size={size}
            animate={animate}
          />
        </>
      )}
    </div>
  );
}
